import React, { PropsWithChildren, useState, useEffect } from 'react';
import { useAskMeAnything } from '../../features/askMeAnything/hooks/useAskMeAnything';
import { Button, Tag, Splitter } from 'antd';
import { Outlet } from 'react-router-dom';
import { ChatPanels } from './components/ChatPanels';
import {
  MainContent,
  Sidebar,
  SidebarContent,
  ToggleContainer,
  ToggleWrapper,
} from './wrapper.styles';
import { CHATS_STORAGE_KEY, CHAT_ID_PREFIX, Chat } from './types';
import { AccountSearchProvider } from '../../features/account/account-search/account-search.context';
import { AccountOverviewProvider } from '../../features/account/account-overview/account-overview.context';
import { Magic } from '../../features/shared/components/Icons/Magic';

export const WrapperAISearch: React.FC<PropsWithChildren<{}>> = () => {
  const { askInsights } = useAskMeAnything();
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => localStorage.getItem('isSidebarOpen') === 'true',
  );
  const [showChatList, setShowChatList] = useState(false);
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<string>('');

  useEffect(() => {
    const storedChats = localStorage.getItem(CHATS_STORAGE_KEY);
    if (storedChats) {
      const parsedChats = JSON.parse(storedChats);
      setChats(parsedChats);
      if (parsedChats.length > 0 && !selectedChat) {
        setSelectedChat(parsedChats[0].chatId);
      }
    }
  }, []);

  const saveChats = (updatedChats: Chat[]) => {
    localStorage.setItem(CHATS_STORAGE_KEY, JSON.stringify(updatedChats));
    setChats(updatedChats);
  };

  const deleteChat = (chatId: string) => {
    const updatedChats = chats.filter(chat => chat.chatId !== chatId);
    saveChats(updatedChats);

    if (selectedChat === chatId) {
      setSelectedChat(updatedChats[0]?.chatId || '');
    }
  };

  const renameChat = (chatId: string, newName: string) => {
    if (!newName.trim()) return;

    const updatedChats = chats.map(chat =>
      chat.chatId === chatId ? { ...chat, chat: newName.trim() } : chat,
    );
    saveChats(updatedChats);
  };

  const createNewChat = () => {
    const newChat: Chat = {
      chat: `New Chat (${chats.length + 1})`,
      chatId: `${CHAT_ID_PREFIX}${Date.now()}`,
      createdAt: new Date().toISOString(),
    };

    const updatedChats = [...chats, newChat];
    saveChats(updatedChats);
    setSelectedChat(newChat.chatId);
    setShowChatList(false);
  };

  const toggleSidebar = () => {
    localStorage.setItem('isSidebarOpen', !isSidebarOpen ? 'true' : 'false');
    setIsSidebarOpen(!isSidebarOpen);

    const thenaElement = document.getElementById('thena-iframe-element');
    if (thenaElement) {
      thenaElement.style.right = isSidebarOpen ? '2%' : '31%';
    }
  };

  const ToggleComponent = () => (
    <ToggleContainer>
      <Button type="primary" onClick={toggleSidebar} icon={<Magic style={{ fill: 'white' }} />}>
        {isSidebarOpen && 'Hide'} AI Assistant
      </Button>
    </ToggleContainer>
  );

  return (
    <>
      <AccountSearchProvider searchId="ai-search">
        <AccountOverviewProvider>
          <Splitter>
            <Splitter.Panel min={'50%'} max={'100%'}>
              <MainContent>
                <ToggleWrapper>
                  <ToggleComponent />
                </ToggleWrapper>

                <Outlet />
              </MainContent>
            </Splitter.Panel>

            {isSidebarOpen && (
              <Splitter.Panel min={'30%'} size={'30%'}>
                <Sidebar>
                  <SidebarContent>
                    <Tag
                      color="red"
                      bordered={false}
                      style={{ width: '100%', borderRadius: '0px 0px 0px 0', padding: '8px' }}
                    >
                      <strong>Currently in Alpha</strong> — share any and all feedback to{' '}
                      <a
                        href="mailto:concierge@update.ai"
                        style={{ color: 'red !important', textDecoration: 'underline !important' }}
                      >
                        concierge@update.ai
                      </a>
                    </Tag>

                    <ChatPanels
                      chats={chats}
                      showList={showChatList}
                      setShowList={setShowChatList}
                      selectedChat={selectedChat || ''}
                      setSelectedChat={setSelectedChat}
                      createNewChat={createNewChat}
                      deleteChat={deleteChat}
                      renameChat={renameChat}
                      askFunction={askInsights}
                      location="ai-search"
                    />
                  </SidebarContent>
                </Sidebar>
              </Splitter.Panel>
            )}
          </Splitter>
        </AccountOverviewProvider>
      </AccountSearchProvider>
    </>
  );
};
